import { Inject, Injectable } from '@angular/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  of,
  throwError,
} from 'rxjs';

import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  CarHttpService,
  DossierHttpService,
} from '@sc/data-access-http-services';
import {
  Appointment,
  Car,
  Claim,
  Customer,
  Document,
  DocumentTypeEnum,
  Dossier,
  DossierToken,
  Environment,
  InterventionParams,
  LicensePlateNumberMask,
  PhoneNumber,
  PhoneNumberMask,
  SearchFilters,
  SinisterGlass,
  getOperationType,
  lang,
} from '@sc/data-access-models';

import {
  ClearDossierAction,
  DossierState,
  PatchAppointmentAction,
  PatchClaimAction,
  PatchDossierAction,
  SetDossierAction,
} from '../../store';

import {
  catchError,
  filter,
  map,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { ENV_CONFIG } from '@sc/data-access-environment';
import {
  AbandonDossierMutationVariables,
  DossierInput,
} from '@sc/data-access-graphql';
import { StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { dayjs } from '@sc/utils';
import { AmPmTranslator } from '../../../../../../../functions/shared/libs/utils/am-pm-translator';
@Injectable({ providedIn: 'root' })
export class DossierSandboxService {
  @Select(DossierState)
  dossier$: Observable<Dossier>;

  @Select((state) => state.dossier?.claim)
  claim$: Observable<Claim>;

  @Select((state) => state.dossier?.customer)
  customer$: Observable<Customer>;

  dossierFetching$: Observable<boolean>;
  private readonly inFetchDossierFunc$ = new BehaviorSubject<boolean>(false);

  storagePlace: Storage;

  get statesComplete() {
    return ['COMPLETE', 'SLOT_SUGGESTED', 'NO_SHOW', 'CANCELLED', 'DONE'];
  }

  constructor(
    private readonly dossierHttpService: DossierHttpService,
    private readonly carHttpService: CarHttpService,
    private readonly configurationService: ConfigurationService,
    private readonly translatorService: TranslatorService,
    public featureFlippingService: FeatureFlippingManagerService,
    private readonly storage: StorageService,
    private readonly timezoneService: TimezoneService,
    @Inject(ENV_CONFIG) protected env: Environment
  ) {
    this.storagePlace =
      env?.auth?.tokenManager?.storage === 'sessionStorage'
        ? sessionStorage
        : localStorage;

    this.dossierFetching$ = combineLatest([
      this.dossier$,
      this.inFetchDossierFunc$,
    ]).pipe(
      map(
        ([dossier, isFetching]) =>
          isFetching ||
          (!dossier &&
            !!this.storage.getItem('dossier-number', this.storagePlace))
      )
    );
  }

  forceIsFetching(isFetching = true) {
    this.inFetchDossierFunc$.next(isFetching);
  }

  getOrFetchDossier(id: string): Observable<Dossier> {
    return this.dossier$.pipe(
      switchMap((currentDossier) => {
        if (currentDossier?.id === id) {
          return of(currentDossier);
        } else {
          return this.fetchDossier(id).pipe(switchMap(() => this.dossier$));
        }
      }),
      take(1)
    );
  }

  @Dispatch()
  fetchCarData(licensePlateNumber: string, damagedPart?: string) {
    return this.carHttpService
      .getCarData({ licensePlateNumber, damagedPart })
      .pipe(
        take(1),
        tap((vehicle: Car) => {
          if (!vehicle || !vehicle.manufacturer) {
            throw throwError('License Plate not found');
          }
        }),
        map((car: Car) => {
          return new PatchClaimAction({
            car: {
              ...car,
              licensePlateNumber: LicensePlateNumberMask.mask(
                licensePlateNumber,
                this.configurationService.getAllLicensePlateCountries()
              ),
              countryCode:
                this.configurationService.getDefaultLicensePlateCountry(),
            },
            calibration: dayjs(car.firstRegistration).isSameOrAfter(
              '2022-07-01'
            )
              ? 'true'
              : dayjs(car.firstRegistration).isBefore('2016-01-01')
              ? 'false'
              : null,
          });
        })
      );
  }

  @Dispatch()
  fetchDossier(id: string): Observable<SetDossierAction> {
    this.inFetchDossierFunc$.next(true);
    return this.dossierHttpService.fetchDossier({ id }).pipe(
      switchMap((dossier) => this.fillPolicyInformation(dossier)),
      take(1),
      map((dossier) => new SetDossierAction(dossier)),
      tap(
        () => this.inFetchDossierFunc$.next(false),
        () => this.inFetchDossierFunc$.next(false)
      )
    );
  }

  @Dispatch()
  fetchDossierBySinisterNumber(
    sinisterNumber: string
  ): Observable<SetDossierAction> {
    this.inFetchDossierFunc$.next(true);
    return this.dossierHttpService
      .fetchDossierBySinisterNumber({ sinisterNumber })
      .pipe(
        switchMap((dossier) => this.fillPolicyInformation(dossier)),
        take(1),
        map((dossier) => new SetDossierAction(dossier)),
        tap(
          () => this.inFetchDossierFunc$.next(false),
          () => this.inFetchDossierFunc$.next(false)
        )
      );
  }

  @Dispatch()
  setDossier(dossier: Partial<Dossier>): Observable<SetDossierAction> {
    return this.fillPolicyInformation(dossier).pipe(
      take(1),
      map((filledDossier) => new SetDossierAction(filledDossier))
    );
  }

  @Dispatch()
  patchDossier(dossier: Partial<Dossier>): Observable<PatchDossierAction> {
    return this.fillPolicyInformation(dossier).pipe(
      take(1),
      map((filledDossier) => new PatchDossierAction(filledDossier))
    );
  }

  @Dispatch()
  clearDossier(): Observable<ClearDossierAction> {
    return of(new ClearDossierAction());
  }

  @Dispatch()
  patchClaim(payload: Partial<Claim>): Observable<PatchClaimAction> {
    return of(new PatchClaimAction(payload));
  }

  @Dispatch()
  patchAppointment(
    payload: Partial<Appointment>
  ): Observable<PatchAppointmentAction> {
    return of(new PatchAppointmentAction(payload));
  }

  patchDossierSearchFilters(
    searchFilters: Partial<
      Omit<SearchFilters, 'interventionParams'> & {
        interventionParams: Partial<InterventionParams>;
      }
    >
  ) {
    return this.dossier$.pipe(
      take(1),
      switchMap((dossier) =>
        this.patchDossier({
          ...dossier,
          searchCriteria: {
            ...dossier?.searchCriteria,
            filters: {
              ...dossier?.searchCriteria?.filters,
              ...searchFilters,
              interventionParams: {
                ...dossier?.searchCriteria?.filters?.interventionParams,
                ...searchFilters?.interventionParams,
              },
            },
          },
        })
      )
    );
  }

  createDossierAccount(): Observable<Partial<Dossier>> {
    return this.dossier$.pipe(
      take(1),
      switchMap((dossier) =>
        this.dossierHttpService.createDossierAccount(dossier)
      )
    );
  }

  updateDossier(isDocUpload = false) {
    return this.dossier$.pipe(
      take(1),
      switchMap((dossier) => {
        if (
          (!dossier?.appointment && !dossier?.requestAppointment) ||
          (((!!dossier?.appointment?.status &&
            dossier?.appointment?.status !== 'MODIFIED') ||
            (!!dossier?.requestAppointment?.status &&
              dossier?.requestAppointment?.status !== 'MODIFIED')) &&
            dossier?.previousAppointments?.length === 0) ||
          isDocUpload
        ) {
          return this.dossierHttpService.updateDossier({
            dossier: dossier as DossierInput,
          });
        } else return of(dossier);
      }),
      map((dossierUpdated) => {
        this.patchDossier(dossierUpdated);
        return dossierUpdated;
      })
    );
  }

  changeDossierLanguage(language: string) {
    this.dossier$
      .pipe(
        take(1),
        filter((dossier) => !!dossier),
        switchMap((dossier) =>
          this.dossierHttpService
            .changeDossierLanguage({ id: dossier.id, language })
            .pipe(map((response) => ({ response, dossier })))
        )
      )
      .subscribe(({ response, dossier }) => {
        if (response === true) {
          this.patchDossier({
            ...dossier,
            selectedLanguage: language as lang,
          });
        }
      });
  }

  createDossierWithPreDeclaration(
    licensePlate: string,
    sinisterDate: Date,
    login: string
  ): Observable<DossierToken> {
    return this.dossierHttpService.createDossierWithPreDeclaration(
      LicensePlateNumberMask.mask(
        licensePlate,
        this.configurationService
          .getConfig()
          .patterns.licensePlate.map((pattern) => pattern.value)
      ),
      sinisterDate,
      login
    );
  }

  createDossierWithOpenArrival(
    firstname: string,
    lastname: string,
    email: string,
    phone: PhoneNumber,
    licensePlate: string,
    sinisterDate: string,
    businessOrigin?: string,
    captchaResponse?: string,
    glass?: SinisterGlass
  ): Observable<DossierToken> {
    const selectedLanguage =
      this.storage.getItem('dossier-language') ||
      this.configurationService.getConfig().defaultLang;
    return this.dossierHttpService.createDossierWithOpenArrival(
      firstname,
      lastname,
      email,
      {
        ...phone,
        number: this.formatCustomerPhoneNumber(phone.number),
      },
      LicensePlateNumberMask.mask(
        licensePlate,
        this.configurationService.getAllLicensePlateCountries()
      ),
      sinisterDate,
      businessOrigin,
      captchaResponse,
      selectedLanguage,
      glass
    );
  }

  private fillPolicyInformation(newDossierInfos: Partial<Dossier>) {
    return this.dossier$.pipe(
      take(1),
      map((currentDossier): Dossier => {
        return {
          ...currentDossier,
          ...newDossierInfos,
          policyInformation: {
            ...currentDossier?.policyInformation,
            ...newDossierInfos.policyInformation,
            insuranceName:
              newDossierInfos.policyInformation?.insuranceName ||
              currentDossier?.policyInformation?.insuranceName ||
              'NONE',
          },
        };
      }),
      catchError((err) => {
        console.error(err);
        return of(newDossierInfos);
      })
    );
  }

  public listenLanguageUpdate() {
    this.translatorService
      .getCurrentLang()
      .pipe(
        skip(1), // Skip the first value to avoid the initial value: dossier language is updated if it is different from the current language
        map((language) => language),
        switchMap((language) =>
          this.dossier$.pipe(
            take(1),
            filter((dossier) => !!dossier),
            map((dossier) => ({ language, dossier }))
          )
        )
      )
      .subscribe(({ language, dossier }) => {
        if (dossier && dossier?.selectedLanguage !== language) {
          this.changeDossierLanguage(language);
        }
      });
  }

  formatCustomerPhoneNumber(phoneNumber: string): string {
    if (phoneNumber) {
      const formattedPhoneNumber = PhoneNumberMask.mask(
        phoneNumber?.replace(/\s/g, ''),
        this.configurationService.getConfig().patterns.phoneNumber
      );
      return formattedPhoneNumber;
    } else return phoneNumber;
  }

  abandonDossier(params: AbandonDossierMutationVariables) {
    return this.dossierHttpService.abandonDossier(params);
  }

  setDossierLanguage(dossierLanguage: lang) {
    this.storage.setItem(
      'dossier-language',
      dossierLanguage,
      this.storagePlace
    );
  }

  clearDossierLanguage() {
    this.storage.removeItem('dossier-language', this.storagePlace);
  }

  getDossierLanguage() {
    return this.storage.getItem('dossier-language', this.storagePlace);
  }

  translatorUse(language) {
    return this.translatorService.use(language);
  }

  getClaimPicturesFromDossierDocuments(documents: Document[]) {
    return (
      documents?.filter(
        (picture) =>
          Object.values(DocumentTypeEnum).findIndex(
            (type) => type === picture?.type
          ) > -1
      ) ?? []
    );
  }

  getNotClaimPicturesFromDossierDocuments(documents: Document[]) {
    return documents?.filter((picture) => picture?.type === 'DOCUMENT') ?? [];
  }

  isRebookOnlySameFitterActive(dossier: Dossier) {
    return (
      this.featureFlippingService.isFeatureActivated(
        'b2c-rebook-only-same-fitter-from-mail'
      ) &&
      dossier.creationData?.origin === 'AGENDA' &&
      (dossier.appointment?.status === 'CANCELLED' ||
        dossier.requestAppointment?.status === 'CANCELLED')
    );
  }

  isModifyOnlySameFitterActive(dossier: Dossier) {
    return (
      this.featureFlippingService.isFeatureActivated(
        'b2c-modify-only-same-fitter-from-mail'
      ) &&
      (dossier.creationData?.origin === 'AGENDA' ||
        dossier.creationData?.origin === 'B2B') &&
      dossier.appointment?.status === 'MODIFIED'
    );
  }

  shouldDisplayAppointmentHalfDay(appointment: Appointment) {
    return (
      appointment?.outsideIntervention &&
      this.featureFlippingService.isBrandFeatureActivated(
        appointment?.brandId,
        'display-PAD-as-half-day'
      )
    );
  }

  getTime(appointment: Appointment) {
    if (
      appointment.outsideIntervention &&
      this.featureFlippingService.isBrandFeatureActivated(
        appointment.brandId,
        'display-PAD-as-half-day'
      )
    ) {
      return this.translatorService.getInstantTranslation(
        dayjs(appointment?.time)
          .tz(this.timezoneService.getTimezone(appointment))
          .format('HH:mm') <
          this.configurationService.getBrandMobilityHalfDayLimit(
            appointment?.brandId
          )
          ? 'GlobalApplicativeConstants.time.morning'
          : 'GlobalApplicativeConstants.time.afternoon'
      );
    }

    const momentDate = dayjs(appointment?.time).tz(
      this.timezoneService.getTimezone(appointment)
    );

    // Get current language for AM/PM translation
    const currentLang = this.translatorService.getCurrentLangValue();

    // Use centralized AM/PM translation utility
    return AmPmTranslator.formatTimeWithAmPm(
      momentDate,
      currentLang,
      this.timezoneService.getTimezone(appointment),
      'LT'
    );
  }

  updateDossierOperation(dossier: Dossier) {
    if (!dossier?.claim?.operationType && dossier?.claim?.glass) {
      const operationType = getOperationType(dossier?.claim?.glass);
      if (operationType) {
        this.patchClaim({ operationType }).subscribe((dossier) =>
          this.updateDossier().subscribe()
        );
      }
    }
  }

  notifyCustomer(dossierId: string) {
    return this.dossierHttpService.notifyCustomer(dossierId);
  }

  sendCustomerAccessEmail(dossierId: string) {
    return this.dossierHttpService.sendCustomerAccessEmail(dossierId);
  }
}
